import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { AuthState, LoginCredentials, User, RegisterData } from '../types/user';
import { auth, db } from '../firebase/config';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile as fbUpdateProfile,
  sendEmailVerification,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

interface AuthContextType {
  authState: AuthState;
  login: (credentials: LoginCredentials) => Promise<User>;
  register: (data: RegisterData) => Promise<User>;
  logout: () => void;
  isAdmin: boolean;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  toggleFavorite: (productId: number) => void;
  isFavorite: (productId: number) => boolean;
  updateUser: (updates: Partial<User>) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  sendPasswordlessLink: (email: string, continuePath?: string) => Promise<void>;
  completePasswordlessLinkSignIn: (url?: string) => Promise<User>;
  reauthWithPassword: (password: string) => Promise<void>;
  reloadAndSyncUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Firestore user doc helpers
const userDocRef = (uid: string) => doc(db, 'users', uid);

const reviveUser = (raw: any): User => ({
  id: raw.id,
  email: raw.email,
  name: raw.name ?? '',
  role: (raw.role as User['role']) ?? 'user',
  favorites: Array.isArray(raw.favorites) ? raw.favorites : [],
  lists: Array.isArray(raw.lists) ? raw.lists.map((l: any) => ({
    ...l,
    createdAt: l.createdAt?.toDate ? l.createdAt.toDate() : new Date(),
    updatedAt: l.updatedAt?.toDate ? l.updatedAt.toDate() : new Date(),
  })) : [],
  createdAt: raw.createdAt?.toDate ? raw.createdAt.toDate() : new Date(),
  updatedAt: raw.updatedAt?.toDate ? raw.updatedAt.toDate() : new Date(),
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  // Subscribe to Firebase Auth state
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setAuthState({ user: null, isAuthenticated: false, isLoading: false, error: null });
        return;
      }
      try {
        const ref = userDocRef(fbUser.uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          // Create an initial profile
          const profile: Omit<User, 'createdAt' | 'updatedAt'> = {
            id: fbUser.uid,
            email: fbUser.email || '',
            name: fbUser.displayName || '',
            role: 'user',
            favorites: [],
            lists: [],
          } as any;
          await setDoc(ref, {
            ...profile,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          } as any, { merge: true });
          const newUser: User = { ...profile, createdAt: new Date(), updatedAt: new Date() } as User;
          setAuthState({ user: newUser, isAuthenticated: true, isLoading: false, error: null });
        } else {
          const data = snap.data();
          const user = reviveUser(data);
          setAuthState({ user, isAuthenticated: true, isLoading: false, error: null });
        }
      } catch (err) {
        console.error('Failed to load user profile:', err);
        setAuthState({ user: null, isAuthenticated: false, isLoading: false, error: 'Failed to load user' });
      }
    });
    return () => unsub();
  }, []);

  // Check if a product is in user's favorites
  const isFavorite = useCallback((productId: number): boolean => {
    return authState.user?.favorites?.includes(productId) || false;
  }, [authState.user]);

  // Toggle favorite status for a product
  const toggleFavorite = useCallback((productId: number) => {
    setAuthState(prev => {
      if (!prev.user) return prev;
      const updatedFavorites = prev.user.favorites.includes(productId)
        ? prev.user.favorites.filter(id => id !== productId)
        : [...prev.user.favorites, productId];
      const updatedUser = { ...prev.user, favorites: updatedFavorites, updatedAt: new Date() };
      // Optimistic update
      (async () => {
        try {
          await updateDoc(userDocRef(prev.user!.id), { favorites: updatedFavorites, updatedAt: serverTimestamp() } as any);
        } catch (e) {
          console.error('Failed to update favorites:', e);
        }
      })();
      return { ...prev, user: updatedUser };
    });
  }, []);

  // --- Email verification helpers ---
  const sendVerificationEmail = useCallback(async () => {
    if (!auth.currentUser) throw new Error('Not authenticated');
    const actionCodeSettings = {
      url: `${window.location.origin}/?verified=1`,
      handleCodeInApp: false,
    };
    await sendEmailVerification(auth.currentUser, actionCodeSettings);
  }, []);

  const resendVerificationEmail = useCallback(async () => {
    await sendVerificationEmail();
  }, [sendVerificationEmail]);

  // --- Passwordless (email link) helpers ---
  const sendPasswordlessLink = useCallback(async (email: string, continuePath = '/auth/callback') => {
    const actionCodeSettings = {
      url: `${window.location.origin}${continuePath}`,
      handleCodeInApp: true,
    };
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);
    window.localStorage.setItem('emailForSignIn', email);
  }, []);

  const completePasswordlessLinkSignIn = useCallback(async (url?: string) => {
    const href = url || window.location.href;
    if (!isSignInWithEmailLink(auth, href)) {
      throw new Error('Invalid or expired sign-in link');
    }
    const stored = window.localStorage.getItem('emailForSignIn');
    const email = stored || window.prompt('Please confirm your email');
    if (!email) throw new Error('Email is required to complete sign-in');
    const cred = await signInWithEmailLink(auth, email, href);
    window.localStorage.removeItem('emailForSignIn');
    // Return a minimal User; full profile will be populated by onAuthStateChanged
    const fbUser = cred.user;
    const user: User = {
      id: fbUser.uid,
      email: fbUser.email || email,
      name: fbUser.displayName || '',
      role: 'user',
      favorites: [],
      lists: [],
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    return user;
  }, []);

  // --- Reauthentication for sensitive ops ---
  const reauthWithPassword = useCallback(async (password: string) => {
    const current = auth.currentUser;
    if (!current || !current.email) throw new Error('Not authenticated');
    const cred = EmailAuthProvider.credential(current.email, password);
    await reauthenticateWithCredential(current, cred);
  }, []);

  // --- Reload Firebase user and sync profile email if changed ---
  const reloadAndSyncUser = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) return;
    await current.reload();
    const freshEmail = current.email || '';
    setAuthState(prev => {
      if (!prev.user) return prev;
      if (prev.user.email === freshEmail) return prev;
      // optimistically update state
      return { ...prev, user: { ...prev.user, email: freshEmail, updatedAt: new Date() } };
    });
    try {
      const uid = current.uid;
      await updateDoc(userDocRef(uid), { email: freshEmail, updatedAt: serverTimestamp() } as any);
    } catch (e) {
      console.error('Failed to sync email to Firestore:', e);
    }
  }, []);

  const login = async (credentials: LoginCredentials) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const { user: fbUser } = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      // onAuthStateChanged will populate profile; return a minimal user until then
      const user: User = {
        id: fbUser.uid,
        email: fbUser.email || credentials.email,
        name: fbUser.displayName || '',
        role: 'user',
        favorites: [],
        lists: [],
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      return user;
    } catch (error) {
      console.error('Login failed:', error);
      setAuthState(prev => ({ ...prev, isLoading: false, error: 'Login failed' }));
      throw error;
    }
  };
  
  const register = async (data: RegisterData) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const cred = await createUserWithEmailAndPassword(auth, data.email, data.password);
      if (data.name) {
        try { await fbUpdateProfile(cred.user, { displayName: data.name }); } catch {}
      }
      const ref = userDocRef(cred.user.uid);
      const profile: Omit<User, 'createdAt' | 'updatedAt'> = {
        id: cred.user.uid,
        email: data.email,
        name: data.name,
        role: 'user',
        favorites: [],
        lists: [],
      } as any;
      await setDoc(ref, { ...profile, createdAt: serverTimestamp(), updatedAt: serverTimestamp() } as any, { merge: true });
      const user: User = { ...profile, createdAt: new Date(), updatedAt: new Date() } as User;
      setAuthState({ user, isAuthenticated: true, isLoading: false, error: null });
      return user;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Registration failed';
      setAuthState(prev => ({ ...prev, user: null, isAuthenticated: false, isLoading: false, error: errorMessage }));
      throw new Error(errorMessage);
    }
  };

  // loginInternal no longer needed; keep signature compatibility if imported elsewhere

  const logout = () => {
    try {
      signOut(auth);
      setAuthState({ user: null, isAuthenticated: false, isLoading: false, error: null });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const updateUser = useCallback(async (updates: Partial<User>) => {
    if (!authState.user) return;
    const ref = userDocRef(authState.user.id);
    const next = { ...updates, updatedAt: serverTimestamp() } as any;
    try {
      await updateDoc(ref, next);
      setAuthState(prev => prev.user ? { ...prev, user: { ...prev.user, ...updates, updatedAt: new Date() } } : prev);
    } catch (e) {
      console.error('Failed to update user:', e);
    }
  }, [authState.user]);

  // Expose the auth state and actions
  const value = {
    authState,
    login,
    register,
    logout,
    isAdmin: authState.user?.role === 'admin',
    isAuthenticated: !!authState.user,
    isEmailVerified: !!auth.currentUser?.emailVerified,
    toggleFavorite,
    isFavorite,
    updateUser,
    sendVerificationEmail,
    resendVerificationEmail,
    sendPasswordlessLink,
    completePasswordlessLinkSignIn,
    reauthWithPassword,
    reloadAndSyncUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!authState.isLoading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
